import React, { MouseEventHandler, PropsWithChildren } from 'react';

import { css, cx } from '@emotion/css';
import {
  Button as MuiButton,
  ButtonClasses as MuiButtonClasses,
} from '@mui/material';

import { useStyles } from '@/hooks/useStyles.ts';
import { Loader } from '@/uiKit/atoms/pending/Loader.tsx';
import { AtomProps, StyleClasses } from '@/uiKit/atoms/utils/AtomTypes.ts';

export type BaseButtonProps = PropsWithChildren<{
  variant: 'contained' | 'outlined' | 'text';
  onClick: MouseEventHandler<HTMLButtonElement>;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  pending?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
}>;

type ButtonClasses = StyleClasses<keyof MuiButtonClasses>;

export type ButtonProps = AtomProps<BaseButtonProps, ButtonClasses>;

/**
 * Button is a React functional component that renders a Material-UI Button.
 *
 * It restricts a MUI Button to a specific set of props:
 * - pendi
 */
export const Button: React.FC<ButtonProps> = ({
  className,
  children,
  pending,
  startIcon,
  endIcon,
  classes,
  ...props
}) => {
  const styles = useStyles(makeStyles);

  return (
    <MuiButton
      {...props}
      startIcon={startIcon}
      endIcon={endIcon}
      loading={pending}
      loadingIndicator={<Loader size="M" />}
      className={cx(className, {
        [styles.iconOnly]: !children && (!!startIcon || !!endIcon),
      })}
    >
      {children}
    </MuiButton>
  );
};

const makeStyles = () => ({
  iconOnly: css`
    min-width: 0;
    aspect-ratio: 1;
    padding: 0.5rem;
    & .MuiButton-icon {
      margin: 0;
    }
  `,
});
